import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SEOPageSettings {
  title: string;
  description: string;
  keywords: string;
  og_image: string;
}

export interface GlobalSEOSettings {
  default_og_image: string;
  global_keywords: string;
  site_name: string;
  pages: Record<string, SEOPageSettings>;
}

const SEO_PAGES = ['home', 'auth', 'dashboard', 'contact', 'policy'] as const;
export type SEOPage = (typeof SEO_PAGES)[number];

const DEFAULT_PAGE: SEOPageSettings = { title: '', description: '', keywords: '', og_image: '' };

export function useSEOSettings() {
  const [settings, setSettings] = useState<GlobalSEOSettings>({
    default_og_image: '',
    global_keywords: '',
    site_name: 'Wopfy',
    pages: {},
  });
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value');
      if (error) throw error;

      const mapped: Record<string, string> = {};
      (data || []).forEach((row: any) => {
        mapped[row.setting_key] = row.setting_value;
      });

      const pages: Record<string, SEOPageSettings> = {};
      for (const page of SEO_PAGES) {
        pages[page] = {
          title: mapped[`seo_${page}_title`] || '',
          description: mapped[`seo_${page}_description`] || '',
          keywords: mapped[`seo_${page}_keywords`] || '',
          og_image: mapped[`seo_${page}_og_image`] || '',
        };
      }

      setSettings({
        default_og_image: mapped.seo_default_og_image || '',
        global_keywords: mapped.seo_global_keywords || '',
        site_name: mapped.site_name || 'Wopfy',
        pages,
      });
    } catch {
      // use defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const getPageSEO = (page: SEOPage): SEOPageSettings => {
    return settings.pages[page] || DEFAULT_PAGE;
  };

  return { settings, loading, refetch: fetchSettings, getPageSEO, SEO_PAGES };
}
